import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

// Simple ID generator for toasts
const generateId = () => `toast_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

export interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  duration?: number;
  dismissible?: boolean;
}

export interface ToastState {
  toasts: Toast[];
}

const initialState: ToastState = {
  toasts: [],
};

export interface AddToastPayload {
  type?: Toast['type'];
  title: string;
  message: string;
  duration?: number;
  dismissible?: boolean;
}

const toastSlice = createSlice({
  name: 'toast',
  initialState,
  reducers: {
    addToast: (state, action: PayloadAction<AddToastPayload>) => {
      const toast: Toast = {
        id: generateId(),
        type: action.payload.type || 'info',
        title: action.payload.title,
        message: action.payload.message,
        duration: action.payload.duration || 5000,
        dismissible: action.payload.dismissible !== false,
      };
      state.toasts.push(toast);
    },

    removeToast: (state, action: PayloadAction<string>) => {
      state.toasts = state.toasts.filter(toast => toast.id !== action.payload);
    },

    clearAllToasts: (state) => {
      state.toasts = [];
    },

    // Helper actions for common toast types
    addSuccessToast: (state, action: PayloadAction<{ title: string; message: string }>) => {
      const toast: Toast = {
        id: generateId(),
        type: 'success',
        title: action.payload.title,
        message: action.payload.message,
        duration: 4000,
        dismissible: true,
      };
      state.toasts.push(toast);
    },

    addErrorToast: (state, action: PayloadAction<{ title: string; message: string }>) => {
      const toast: Toast = {
        id: generateId(),
        type: 'error',
        title: action.payload.title,
        message: action.payload.message,
        duration: 8000, // Longer duration for errors
        dismissible: true,
      };
      state.toasts.push(toast);
    },

    addWarningToast: (state, action: PayloadAction<{ title: string; message: string }>) => {
      const toast: Toast = {
        id: generateId(),
        type: 'warning',
        title: action.payload.title,
        message: action.payload.message,
        duration: 6000,
        dismissible: true,
      };
      state.toasts.push(toast);
    },

    addInfoToast: (state, action: PayloadAction<{ title: string; message: string }>) => {
      const toast: Toast = {
        id: generateId(),
        type: 'info',
        title: action.payload.title,
        message: action.payload.message,
        duration: 5000,
        dismissible: true,
      };
      state.toasts.push(toast);
    },
  },
});

export const {
  addToast,
  removeToast,
  clearAllToasts,
  addSuccessToast,
  addErrorToast,
  addWarningToast,
  addInfoToast,
} = toastSlice.actions;

export default toastSlice.reducer;
